import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import { Backdrop, CircularProgress, Button, Breadcrumbs, Typography, Link } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import LoginHeader from "../components/LoginHeader.js";
import SideNavigation from "../components/SideNavigation.js";
import AADHeader from "../components/AADHeader.js";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Dropdown } from "primereact/dropdown";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import { Tooltip } from "@mui/material";
import { BlobServiceClient } from "@azure/storage-blob";
import "../table.css";
import "./brsr.css";
import BrsrErrorHandler from "./utils/errorHandler";
import BrsrValidator from "./utils/validator";
import BrsrCache from "./utils/cache";
import { API_ENDPOINTS } from "../components/repository";

const BrsrRepositoryAddLink = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const companyid = localStorage.getItem("companyid");
  const username = localStorage.getItem("userName");
  const organization = localStorage.getItem("organization");
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [formData, setFormData] = useState({
    linkName: "",
    linkCategory: "",
    url: "",
    fileName: "",
    fileCategory: "",
    itemType: "file",
  });
  const [errors, setErrors] = useState({});
  const reportIdFromUrl = new URLSearchParams(window.location.search).get("reportId");
  const currentReportId = reportIdFromUrl || localStorage.getItem("brsr_report_id");

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const report_name = localStorage.getItem("report_name");
    console.log(report_name);
    if (report_name === "" || report_name === null || report_name === "undefined") {
      Swal.fire({
        icon: "error",
        html: '<h4 style="color:#407775;font-size: 20px;">Error!</h4><p style="font-size:16px;">Report name is empty</p>',
        iconHtml: '<img src="/assets/img/error.png" style="width: 55px; height: 55px;" />',
        confirmButtonText: "Continue",
      }).then((result) => {
        if (result.isConfirmed && props.onSuccess) {
          props.onSuccess();
        }
      });
    }
  }, []);

  const validateForm = () => {
    let formErrors = {};

    if (formData.itemType === "file") {
      // Enhanced file validation using validator utility
      const fileErrors = BrsrValidator.validateFileData({
        fileName: formData.fileName,
        fileCategory: formData.fileCategory,
        file: formData.file,
      });
      formErrors = { ...formErrors, ...fileErrors };
    } else if (formData.itemType === "link") {
      // Enhanced link validation using validator utility
      const linkErrors = BrsrValidator.validateLinkData({
        linkName: formData.linkName,
        linkCategory: formData.linkCategory,
        url: BrsrValidator.sanitizeUrl(formData.url),
      });
      formErrors = { ...linkErrors };

      // Duplicate URL check against existing links
      if (!formErrors.url) {
        const existingLinks = Array.isArray(props.existingLinks) ? props.existingLinks : [];
        const normalizedUrl = BrsrValidator.sanitizeUrl(formData.url).toLowerCase();
        const isDuplicate = existingLinks.some(
          (link) => (link.Link || link["File Link"] || link.url || "").toLowerCase() === normalizedUrl
        );
        if (isDuplicate) {
          formErrors.url = "This URL has already been added to the repository.";
        }
      }
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const isValidUrl = (urlString) => {
    return BrsrValidator.isValidUrl(urlString);
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    let newEntry;

    if (formData.itemType === "file") {
      newEntry = {
        fileName: formData.fileName,
        fileCategory: formData.fileCategory,
        file: formData.file,
      };
      const tags = {
        category: formData.fileCategory,
        username: organization,
        upload_datetime: new Date().toISOString(),
      };
      const onSuccess = async () => {
        try {
          const report_name = localStorage.getItem("report_name");
          console.log(formData.file.name);
          let response = await fetch(`${process.env.REACT_APP_BASE_API_URL}add-files`, {
            method: "POST",
            headers: {
              "Content-type": "application/json; charset=UTF-8",
              username: username,
            },
            body: JSON.stringify({
              company_id: companyid,
              report_name: report_name,
              file_name: formData.file.name,
              environment: "Dev",
              report_type: "brsr",
            }),
          });
          if (response.status === 200) {
            const responseJson = await response.json();
            if (responseJson.success == false) {
              setLoading(false);
              setFormData({
                linkName: "",
                linkCategory: "",
                url: "",
                fileName: "",
                fileCategory: "",
                file: null,
                itemType: "file",
              });
            } else if (responseJson.status == 400) {
              setLoading(false);
              setFormData({
                linkName: "",
                linkCategory: "",
                url: "",
                fileName: "",
                fileCategory: "",
                file: null,
                itemType: "file",
              });
            } else {
              setLoading(false);
              const resetForm = () => setFormData({ linkName: "", linkCategory: "", url: "", fileName: "", fileCategory: "", file: null, itemType: "file" });
              setLoading(false);
              resetForm();
              if (props.onSuccess) props.onSuccess();
              if (responseJson.status === "success") {
                Swal.fire({
                  icon: "success",
                  html: `<h4 style="color:#407775;font-size: 20px;">File successfully uploaded</h4><p style="font-size:16px;">Analysis triggered</p>`,
                  iconHtml: '<img src="/assets/img/successicon.png" style="width: 55px; height: 55px;" />',
                  confirmButtonText: "Continue",
                });
              } else {
                Swal.fire({
                  icon: "success",
                  html: `<h4 style="color:#407775;font-size: 20px;">${responseJson.status}!</h4><p style="font-size:16px;">${responseJson.message}!</p>`,
                  iconHtml: '<img src="/assets/img/successicon.png" style="width: 55px; height: 55px;" />',
                  confirmButtonText: "Continue",
                });
              }
            }
          }
        } catch (error) {
          console.error(error);
        }
      };
      uploadFile("ecoprismfilecontainer", companyid, formData.file, tags, onSuccess);
    } else if (formData.itemType === "link") {
      setLoading(true);
      const sanitizedUrl = BrsrValidator.sanitizeUrl(formData.url);
      const report_name = localStorage.getItem("report_name");

      try {
        // Step 1 — scrape link into Azure Blob as .md file
        const addLinksResponse = await fetch(`${process.env.REACT_APP_BASE_API_URL}${API_ENDPOINTS.ADD_LINKS}`, {
          method: "POST",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            username: username,
          },
          body: JSON.stringify({
            company_id: companyid,
            report_name: report_name,
            report_type: "brsr",
            links: [sanitizedUrl],
            link_metadata: [{ url: sanitizedUrl, name: formData.linkName, category: formData.linkCategory }],
          }),
        });

        if (addLinksResponse.status !== 200) {
          setLoading(false);
          BrsrErrorHandler.showError("API_ERROR", "Failed to submit link. Please try again.");
          return;
        }

        const addLinksText = await addLinksResponse.text();
        const addLinksJson = addLinksText ? JSON.parse(addLinksText) : {};
        const taskId = addLinksJson.task_id;

        // Step 2 — poll until scraping is complete (only if backend gave us a task_id to track)
        let scrapingTimedOut = false;
        const completedStatus = taskId ? await new Promise((resolve, reject) => {
          let attempts = 0;
          const maxAttempts = 120; // 120 × 5s = 10 minutes
          const qs = new URLSearchParams();
          if (report_name) qs.set("report_name", report_name);
          if (taskId) qs.set("task_id", taskId);
          const qsStr = qs.toString() ? `?${qs.toString()}` : "";
          const interval = setInterval(async () => {
            attempts++;
            try {
              const statusResponse = await fetch(
                `${process.env.REACT_APP_BASE_API_URL}${API_ENDPOINTS.GET_REPO_STATUS}/${companyid}${qsStr}`
              );
              const statusJson = await statusResponse.json();
              if (statusJson.stage === "Completed") {
                clearInterval(interval);
                resolve(statusJson);
              } else if (statusJson.stage === "Failed") {
                clearInterval(interval);
                reject(new Error(statusJson.message || "Scraping failed. Please try again."));
              } else if (attempts >= maxAttempts) {
                clearInterval(interval);
                scrapingTimedOut = true;
                resolve(null); // link was saved — close gracefully
              }
            } catch (err) {
              clearInterval(interval);
              reject(err);
            }
          }, 5000);
        }) : null; // no task_id — scraping is async, proceed to training

        const scrapedBlobName = completedStatus?.scraped_blob_names?.[0] || null;

        // Step 3 — trigger training on the new blob
        const trainResponse = await fetch(`${process.env.REACT_APP_BASE_API_URL}${API_ENDPOINTS.ADD_FILES}`, {
          method: "POST",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            username: username,
          },
          body: JSON.stringify({
            company_id: companyid,
            report_name: report_name,
            report_type: "brsr",
            ...(scrapedBlobName && { file_name: scrapedBlobName }),
          }),
        });

        if (!scrapingTimedOut) {
          if (!trainResponse.ok) {
            throw new Error("Training request failed. The link was saved but analysis may not have started.");
          }
        }

        // Invalidate cache
        const reportId = localStorage.getItem("brsr_report_id");
        if (reportId) {
          BrsrCache.invalidateReportCache(reportId);
        }

        setLoading(false);
        setFormData({ linkName: "", linkCategory: "", url: "", fileName: "", fileCategory: "", file: null, itemType: "file" });
        if (props.onSuccess) props.onSuccess();
        const successMessage = scrapingTimedOut
          ? "<h4 style=\"color:#407775;font-size: 20px;\">Link saved</h4><p style=\"font-size:16px;\">Analysis is running in the background. The status will update automatically.</p>"
          : "<h4 style=\"color:#407775;font-size: 20px;\">Link successfully submitted</h4><p style=\"font-size:16px;\">Analysis triggered</p>";
        Swal.fire({
          icon: "success",
          html: successMessage,
          iconHtml: '<img src="/assets/img/successicon.png" style="width: 55px; height: 55px;" />',
          confirmButtonText: "Continue",
        });
      } catch (error) {
        console.error(error);
        setLoading(false);
        BrsrErrorHandler.showError("API_ERROR", error.message || "An unexpected error occurred. Please try again.");
      }
    }
  };

  useImperativeHandle(ref, () => ({
    submit: handleSave,
  }));

  const fileOptions = [
    { label: "Policy", value: "Policy" },
    { label: "Governance structure", value: "Governance structure" },
    { label: "Governance document", value: "Governance document" },
    { label: "Annual Report", value: "Annual Report" },
    { label: "ESG/Sustainability Report", value: "ESG/Sustainability Report" },
    { label: "GRI Report", value: "GRI Report" },
    { label: "SASB Report", value: "SASB Report" },
    { label: "CDP Report", value: "CDP Report" },
    { label: "TCFD Report", value: "TCFD Report" },
    { label: "Integrated Report", value: "Integrated Report" },
    { label: "ESG Strategy", value: "ESG Strategy" },
    { label: "Roadmap", value: "Roadmap" },
  ];

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, file });
    if (file) {
      setErrors((prevErrors) => ({ ...prevErrors, file: "" }));
    }
  };

  async function uploadFile(containerName, folderName, file, tags, onSuccess) {
    const report_name = localStorage.getItem("report_name");
    try {
      let res = await fetch(`${process.env.REACT_APP_BASE_API_URL}GetSASToken`, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      });
      if (res.status === 200) {
        const responseJson = await res.json();
        const connectionString = responseJson.data;
        const blobServiceClient = new BlobServiceClient(connectionString);
        const containerClient = blobServiceClient.getContainerClient(containerName);
        const blobName = `${folderName}/repository/${report_name}/${file.name}`;
        const blobClient = containerClient.getBlobClient(blobName);
        const blockBlobClient = blobClient.getBlockBlobClient();

        setUploading(true);
        setProgress(0);
        const uploadResponse = await blockBlobClient.uploadBrowserData(file, {
          blobHTTPHeaders: { blobContentType: file.type },
          blockSize: 4 * 1024 * 1024,
          concurrency: 20,
          onProgress: (ev) => {
            const percentComplete = Math.round((ev.loadedBytes / file.size) * 100);
            setProgress(percentComplete);
          },
        });
        setUploading(false);
        if (uploadResponse) {
          console.log(`File "${file.name}" uploaded successfully.`);
          console.log(`Blob URL: ${blobClient.url}`);
          localStorage.setItem("weblink", blobClient.url);
          await blockBlobClient.setMetadata(tags);
          console.log(`Set metadata on blob "${blobName}": ${JSON.stringify(tags)}`);
          onSuccess();
        }
      } else {
        console.error("Failed to fetch SAS token.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  }

  const handleFileDelete = () => {
    setFormData({ ...formData, file: null });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.target.classList.add("drag-over");
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.target.classList.remove("drag-over");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.target.classList.remove("drag-over");

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setFormData({ ...formData, file: files[0] });
    }
  };

  return (
    <div className="form-part add-repo">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>

      <form className="edit-form">
        <div className="row">
          <div className="col">
            <FormControl component="fieldset">
              <RadioGroup row value={formData.itemType} onChange={(e) => setFormData({ ...formData, itemType: e.target.value })} name="itemType">
                <FormControlLabel value="file" control={<Radio />} label="File" />
                <FormControlLabel value="link" control={<Radio />} label="Link" />
              </RadioGroup>
            </FormControl>
          </div>
        </div>

        {formData.itemType === "file" && (
          <div className="row file-row g-5">
            <div className="col-md-12">
              <div className="form-group">
                <label className="formlable" htmlFor="fileName">
                  Name of the File<span className="mark-form">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.fileName ? "is-invalid" : ""}`}
                  id="fileName"
                  placeholder="Enter name of the file"
                  value={formData.fileName}
                  onChange={(e) => {
                    setFormData({ ...formData, fileName: e.target.value });
                    setErrors((prevErrors) => ({ ...prevErrors, fileName: "" }));
                  }}
                />
                {errors.fileName && <div className="invalid-feedback">{errors.fileName}</div>}
              </div>

              <div className="form-group">
                <label className="formlable" htmlFor="fileCategory">
                  Category<span className="mark-form">*</span>
                </label>
                <Dropdown
                  id="fileCategory"
                  className={`form-control wd-100 ${errors.fileCategory ? "is-invalid" : ""}`}
                  value={formData.fileCategory}
                  options={fileOptions}
                  placeholder="Select category"
                  required
                  appendTo={document.body}
                  showClear
                  virtualScrollerOptions={{ itemSize: 38 }}
                  onChange={(e) => {
                    setFormData({ ...formData, fileCategory: e.value });
                    setErrors((prevErrors) => ({ ...prevErrors, fileCategory: "" }));
                  }}
                />
                {errors.fileCategory && <div className="invalid-feedback">{errors.fileCategory}</div>}
              </div>

              <div className="form-group">
                <label className="formlable" htmlFor="fileUpload">
                  Upload File<span className="mark-form">*</span>
                </label>
                <label htmlFor="fileUpload" className="file-upload-box" onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
                  <input type="file" id="fileUpload" onChange={handleFileChange} className="file-input" />
                  <div className="upload-icon">
                    <img src="/assets/img/reports/upload-file.svg" className="" alt="Upload" />
                  </div>
                  <p>
                    <span>Browse</span> or drag and drop here
                  </p>
                </label>

                {formData.file && (
                  <div className="file-preview">
                    <span className="file-name">{formData.file.name}</span>
                    <Tooltip title="Delete" arrow>
                      <img src="/assets/img/reports/delete-repo.svg" alt="Delete Icon" className="delete-icon actions-icon" onClick={handleFileDelete} />
                    </Tooltip>
                  </div>
                )}

                {errors.file && <div className="invalid-feedback d-block">{errors.file}</div>}

                {uploading && (
                  <div className="mt-2">
                    <div className="progress">
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${progress}%`, backgroundColor: "#407775" }}
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {progress}%
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {formData.itemType === "link" && (
          <div className="row link-row g-5">
            <div className="col-md-12">
              <div className="form-group">
                <label className="formlable" htmlFor="linkName">
                  Name of the Link<span className="mark-form">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.linkName ? "is-invalid" : ""}`}
                  id="linkName"
                  placeholder="Enter name of the link"
                  value={formData.linkName}
                  onChange={(e) => {
                    setFormData({ ...formData, linkName: e.target.value });
                    setErrors((prevErrors) => ({ ...prevErrors, linkName: "" }));
                  }}
                />
                {errors.linkName && <div className="invalid-feedback">{errors.linkName}</div>}
              </div>

              <div className="form-group">
                <label className="formlable" htmlFor="linkCategory">
                  Category<span className="mark-form">*</span>
                </label>
                <Dropdown
                  id="linkCategory"
                  className={`form-control wd-100 ${errors.linkCategory ? "is-invalid" : ""}`}
                  value={formData.linkCategory}
                  options={fileOptions}
                  placeholder="Select category"
                  required
                  appendTo={document.body}
                  showClear
                  virtualScrollerOptions={{ itemSize: 38 }}
                  onChange={(e) => {
                    setFormData({ ...formData, linkCategory: e.value });
                    setErrors((prevErrors) => ({ ...prevErrors, linkCategory: "" }));
                  }}
                />
                {errors.linkCategory && <div className="invalid-feedback">{errors.linkCategory}</div>}
              </div>

              <div className="form-group">
                <label className="formlable" htmlFor="url">
                  URL<span className="mark-form">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.url ? "is-invalid" : ""}`}
                  id="url"
                  placeholder="Enter URL"
                  value={formData.url}
                  onChange={(e) => {
                    setFormData({ ...formData, url: e.target.value });
                    setErrors((prevErrors) => ({ ...prevErrors, url: "" }));
                  }}
                />
                {errors.url && <div className="invalid-feedback">{errors.url}</div>}
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
});

export default BrsrRepositoryAddLink;
